import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Loader2 } from 'lucide-react';
import { Lead, LeadStatus } from '../../types/lead';
import { createLead } from '../../lib/leadsManagement';
import { getStages } from '../../lib/stagesManagement';

interface AddLeadModalProps {
  onClose: () => void;
  onCreated: (lead: Lead) => void;
  currentUserId?: string;
  defaultStageId?: string;
}

const AddLeadModal: React.FC<AddLeadModalProps> = ({ onClose, onCreated, currentUserId, defaultStageId }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [website, setWebsite] = useState('');
  const [source, setSource] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError('');

    // Stage "Neue Anfrage" direkt aus Supabase holen
    let stageId = defaultStageId || null;
    if (!stageId) {
      const stages = await getStages();
      stageId = stages.find(s => s.name === 'Neue Anfrage')?.id || null;
    }

    const result = await createLead({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      company: company.trim() || undefined,
      website: website.trim() || undefined,
      source: source.trim() || undefined,
      status: 'neu' as LeadStatus,
      status_changed_at: new Date().toISOString(),
      notes: '',
      facebook_lead_id: '',
      assigned_user_id: currentUserId || null,
      stage_id: stageId,
      meta_data: {},
      archived: false,
      custom_fields: {},
    });

    if (result.success && result.leadId) {
      // Fetch the created lead
      const lead: Lead = {
        id: result.leadId,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        company: company.trim() || undefined,
        website: website.trim() || undefined,
        source: source.trim() || undefined,
        status: 'neu',
        status_changed_at: new Date().toISOString(),
        notes: '',
        facebook_lead_id: '',
        assigned_user_id: currentUserId || null,
        created_at: new Date().toISOString(),
        meta_data: {},
        archived: false,
        custom_fields: {},
        stage_id: stageId,
      };
      onCreated(lead);
      onClose();
    } else {
      setError(result.error || 'Fehler beim Erstellen');
    }
    setSaving(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="relative z-10 bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden"
        >
          <div className="bg-zinc-900 px-5 py-4 flex items-center justify-between">
            <h2 className="text-lg font-black text-white uppercase tracking-widest">Neue Anfrage</h2>
            <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-3">
            <div>
              <label className="text-xs font-semibold text-zinc-500 uppercase tracking-widest block mb-1">Name *</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                autoFocus
                className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                placeholder="Vollständiger Name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-zinc-500 uppercase tracking-widest block mb-1">E-Mail</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  placeholder="email@beispiel.de"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-500 uppercase tracking-widest block mb-1">Telefon</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  placeholder="+49 123 456789"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-zinc-500 uppercase tracking-widest block mb-1">Firma</label>
                <input
                  type="text"
                  value={company}
                  onChange={e => setCompany(e.target.value)}
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  placeholder="Firmenname"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-500 uppercase tracking-widest block mb-1">Website</label>
                <input
                  type="text"
                  value={website}
                  onChange={e => setWebsite(e.target.value)}
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  placeholder="www.beispiel.de"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-500 uppercase tracking-widest block mb-1">Quelle</label>
              <input
                type="text"
                value={source}
                onChange={e => setSource(e.target.value)}
                className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                placeholder="z.B. Facebook, Empfehlung, Web..."
              />
            </div>

            {error && <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-zinc-200 rounded-xl text-sm font-semibold hover:bg-zinc-50 transition-colors">
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={!name.trim() || saving}
                className="flex-1 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-semibold disabled:opacity-40 hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {saving ? 'Speichern...' : 'Anfrage erstellen'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddLeadModal;
